/* eslint-disable @typescript-eslint/no-base-to-string */

import type { TileJoinOptions, TippecanoeOptions } from "@project/api";
import { initial, last } from "es-toolkit/compat";

type Arg = string | [string, string];

const SUPPORTED_SUFFIXES = [
  ".fgb",
  ".ndgeojson",
  ".geojsonl",
  ".geojsonseq",
];

function replaceExt(filename: string, newExt: string): string {
  const idx = filename.lastIndexOf(".");
  return idx === -1 ? `${filename}${newExt}` : `${filename.substring(0, idx)}${newExt}`;
}

export function needsConversion(filename: string): boolean {
  if (!filename) return false;
  return !SUPPORTED_SUFFIXES.some(ext => filename.endsWith(ext));
}

export function convertedName(filename: string): string {
  return replaceExt(filename, ".geojsonseq");
}

/** Safe POSIX shell-quote: leaves plain identifiers unquoted, single-quotes everything else. */
export function shellQuote(value: string): string {
  if (/^[A-Za-z0-9_\-.:/=+@,]+$/.test(value)) return value;
  return `'${value.replaceAll("'", "'\\''")}'`;
}

const formatOption = ([flag, value]: [string, string]): string => {
  return `${flag}=${shellQuote(value)}`;
};

function formatCommand(argv: readonly Arg[]): string {
  const parts: string[] = [];

  for (const arg of argv) {
    if (Array.isArray(arg)) {
      parts.push(formatOption(arg));
    }
    else {
      parts.push(shellQuote(arg));
    }
  }

  const init = initial(parts);
  const final = last(parts);

  return `${init.map(p => `  ${p}`).join(" \\\n")} \n ${final}`;
}

/**
 * Convert an options record (kebab-case keys) to argv flag pairs.
 * Empty-string / empty-array filtering is handled by `cleanOptions` upstream.
 */
function optionsToArgs(options: Record<string, unknown>): Arg[] {
  const args: Arg[] = [];
  for (const [key, value] of Object.entries(options)) {
    const flag = `--${key}`;

    if (typeof value === "boolean") {
      if (value) {
        args.push(flag);
      }
      continue;
    }

    if (Array.isArray(value)) {
      for (const item of value) {
        if (typeof item === "object") {
          args.push([flag, JSON.stringify(item)]);
        }
        else {
          args.push([flag, String(item)]);
        }
      }
      continue;
    }
    if (typeof value === "object") {
      args.push([flag, JSON.stringify(value)]);
      continue;
    }
    else {
      args.push([flag, String(value)]);
      continue;
    }
  }
  return args;
}

/**
 * Build an ogr2ogr command converting an unsupported source to GeoJSONSeq.
 * Mirrors `ogr2ogr_format_command` in the Dagster side.
 */
export function buildOgr2ogrCommand(input: string, output: string): string {
  const inputArg = input.endsWith(".zip") ? `/vsizip/${input}` : input;
  const argv: Arg[] = [
    "ogr2ogr",
    ["-f", "GeoJSONSeq"],
    output,
    inputArg,
  ];
  return formatCommand(argv);
}

/**
 * Build a tippecanoe command matching `build_tippecanoe_args` in the Dagster side.
 * `options.layer` must be set; the function does not inject it.
 */
export function buildTippecanoeCommand(
  inputs: readonly string[],
  output: string,
  options: Partial<TippecanoeOptions>,
): string {
  const argv: Arg[] = [
    "tippecanoe",
    "--force",
  ];

  argv.push(...optionsToArgs(options));
  argv.push(["--output", output]);
  argv.push(...inputs);

  return formatCommand(argv);
}

/**
 * Build a tile-join command matching `build_tile_join_args` in the Dagster side.
 */
export function buildTileJoinCommand(
  inputs: readonly string[],
  output: string,
  options: Partial<TileJoinOptions>,
): string {
  const argv: Arg[] = [
    "tile-join",
    "--force",
  ];

  argv.push(...optionsToArgs(options));
  argv.push(["--output", output]);
  argv.push(...inputs);

  return formatCommand(argv);
}
